<template>
    <div class="container-fluid form-container aldi-form-container">
        <div class="row">
            <div class="col-sm-12">
                <h1 class="page-title">
                    Audit Report
                </h1>
            </div>
        </div>

        <form
            action=""
            class="aldi-form"
            @submit.prevent="saveForm()"
        >
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="customer">Customer</label>
                            <customer-search
                                ref="customerSearch"
                                :customer="form.customer"
                                @change="customerChanged"
                                :customer-id="form.customer_id"
                            />
                            <error
                                :errors="errors"
                                name="customer_id"
                            />
                        </div>
                        <div
                            class="col-sm-12 col-md-6 form-group"
                            v-if="form.customer && form.customer.sites"
                        >
                            <label for="site">Site:</label>
                            <select
                                name="site"
                                v-model="form.site_id"
                                id="site"
                                class="form-control"
                                placeholder="Site"
                                required
                                v-if="form.customer.sites.length"
                            >
                                <option value="">
                                    Select Site
                                </option>
                                <option
                                    v-for="site in form.customer.sites"
                                    :key="site.id"
                                    :value="site.id"
                                >
                                    #{{ site.site_no }} {{ site.name }}
                                </option>
                            </select>
                            <span
                                class="form-error"
                                v-if="!form.customer.sites.length"
                            >This customer has no sites</span>
                            <error
                                :errors="errors"
                                name="site_id"
                            />
                        </div>
                    </div>

                    <div class="row">
                        <div
                            class="col-sm-12 col-md-6 form-group"
                            v-if="form.customer_id"
                        >
                            <label for="job_id">Job No.</label>
                            <select
                                name="job_id"
                                id="job_id"
                                v-model="form.job_id"
                                class="form-control"
                                placeholder="Select a job"
                                :disabled="!jobs"
                            >
                                <option
                                    :value="null"
                                    disabled
                                >
                                    Select a job
                                </option>
                                <option
                                    v-for="job in jobs"
                                    :key="job.id"
                                    :value="job.id"
                                >
                                    {{ job.id }}: {{ job.name }}
                                </option>
                            </select>
                            <error
                                name="job_id"
                                :errors="errors"
                            />
                        </div>

                        <div class="col-sm-12 col-md-3 form-group">
                            <label for="form_date">Date</label>
                            <date-picker
                                v-model="form.form_date"
                                id="form_date"
                                :options="{format: 'L'}"
                                required
                            />
                            <error
                                :errors="errors"
                                name="form_date"
                            />
                        </div>
                        <div class="col-sm-8 col-md-4">
                            <label class="top">Engineer:</label>
                            <br>
                            <user-select
                                v-model="form.engineer_id"
                                :office="false"
                                :admins="false"
                            />
                        </div>

                        <div class="col-sm-8 col-md-2">
                            <label class="top">Order No.</label>
                            <input
                                type="text"
                                class="form-control"
                                v-model="form.order_no"
                                placeholder="Order No."
                            > 
                        </div>
                    </div>
                </div>
            </div>

            <audit-rows
                v-model="form.report_rows"
            />

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <button
                                class="btn btn-success"
                                type="submit"
                                :disabled="isSaving || isLoading"
                            >
                                Save
                            </button>

                            <button
                                class="btn btn-outline-danger"
                                type="button"
                                @click="deleteForm()"
                                :disabled="isSaving || isLoading"
                                v-if="form.id && canDelete"
                            >
                                <i class="fa fa-times" /> Delete
                            </button>

                            <a
                                class="btn btn-info"
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/stream'"
                                target="_blank"
                            >
                                <i class="fa fa-print" /> Preview/Print
                            </a>

                            <a
                                class="btn btn-info"
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/download'"
                                target="_blank"
                                :download="form.doc_no + '.pdf'"
                            >
                                <i class="fa fa-file" /> Download PDF
                            </a>

                            <router-link
                                class="btn btn-info"
                                v-if="form.id"
                                :to="{name: 'office.emails.new', params: {customer_id: form.customer_id}, query: {form_id: form.id}}"
                            >
                                <i class="fa fa-envelope" /> Send to Customer
                            </router-link>

                            <router-link
                                :to="{name: 'engineer.jobs.show', params: {id: form.job_id}}"
                                class="btn btn-info"
                                v-if="$route.name === 'engineer.forms.edit' && form.job_id"
                            >
                                <i class="fa fa-chevron-left" /> Back to Job
                            </router-link>

                            <router-link
                                :to="{name: 'office.jobs.show', params: {id: form.job_id}}"
                                class="btn btn-info"
                                v-if="$route.name === 'office.forms.edit' && form.job_id"
                            >
                                <i class="fa fa-briefcase" /> View Job
                            </router-link>

                            <customer-site-footer-links
                                :customer-id="form.customer_id"
                                :site-id="form.site_id"
                            />

                            <activity-log
                                v-if="form.id"
                                :id="form.id"
                                model="Form"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import datePicker from "../DateTimePickerComponent.vue";
import CustomerSearchComponent from "../CustomerSearch.vue";
import Service from "../../services/FormService";
import AuthService from "../../services/AuthService";
import FormService from "../../services/FormService";
import JobService from "../../services/JobService";
import UploadService from "../../services/UploadService";
import UsesErrors from "../../mixins/UsesErrors";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";
import UsesFormRouteQuery from "../../mixins/UsesFormRouteQuery";
import AuditRows from "./parts/AuditRows.vue";

export default {

    components: {
        AuditRows,
        "customer-search": CustomerSearchComponent,
        "date-picker": datePicker
    },
    mixins: [UsesErrors, UsesInfoMessage, UsesFormRouteQuery],

    props: {
        formId: {
            type: [String, Number],
            default: null,
        }
    },

    data() {
        return {
            form: {
                type: "aldi",
                customer: {
                    company_name: ""
                },
                report_rows: [
                    {},
                ],
                form_date: window.moment(),
            },
            sites: [],
            jobs: [],
            customerSignature: null,
            token: null
        };
    },
    computed: {
        canDelete: function() {
            return AuthService.user.admin_access || AuthService.user.office_access;
        }
    },

    mounted() {
        this.populateFormFromRouteQuery();

        if (this.$route.params.id) {
            const id = this.$route.params.id;
            this.loadForm(id);
        } else {
            FormService.nextNo().then(res => {
                this.form.doc_no = res.data;
            });
        }
    },

    methods: {
        loadForm: function(id) {
            this.setLoading();
            FormService.get(id)
                .then(
                    res => {
                        this.form = res.data;

                        if (!this.form.customer) {
                            this.form.customer = {
                                company_name: ""
                            };
                        }
            
                        if(this.form.form_date) {
                            this.form.form_date = window.moment(this.form.form_date);
                        } else {
                            this.form.form_date = window.moment();
                        }

                        if (!this.form.report_rows) {
                            this.form.report_rows = [];
                        }

                        if (this.form.engineer_id) {
                            this.form.engineer_id = parseInt(this.form.engineer_id);
                        }

                        this.getJobs();
                    },
                    err => {
                        this.setErrorMessage("Could not load form");
                        console.error(err);
                    }
                )
                .then(() => {
                    this.clearLoading();
                });
        },
        customerChanged: function(customer) {
            this.form.customer = customer ? customer : {};
            if (this.form.customer) {
                this.form.customer_id = this.form.customer.id;
            } else {
                this.form.customer_id = null;
            }
            if (customer && customer.sites) {
                if (!customer.sites.map(site => site.id).includes(this.form.site_id)) {
                    this.form.site_id = '';
                }
            } else {
                this.form.site_id = '';
            }
            this.getJobs();
        },

        getJobs: function() {
            if (!this.form.customer_id) {
                this.jobs = [];
                this.form.job_id = null;
                return;
            }
            JobService.search({ "filter[customer_id]": this.form.customer_id })
                .then(res => {
                    this.jobs = res.data.data || [];
                })
                .catch(() => {
                    this.showErrorMessage("Error: Could not load customer jobs");
                    this.jobs = [];
                });
        },

        saveForm: function() {
            this.setSaving();
            this.clearErrors();
            if (
                this.$refs.customerSignature &&
                !this.$refs.customerSignature.isEmpty()
            ) {
                this.form.customer_signature = this.$refs.customerSignature.toPng();
            }

            let data = { ...this.form };
            delete data["customer"];
            delete data["site"];
            delete data["job"];
            delete data["data"];

            if (this.form.id) {
                Service.update(data).then(
                    res => {
                        this.clearSaving();
                        this.setInfoMessage("Form saved successfully");
                        this.$emit("formSaved", res.data);
                    },
                    err => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    }
                );
            } else {
                Service.create(data).then(
                    res => {
                        this.clearSaving();
                        this.setInfoMessage("Form saved successfully");
                        if (this.$route.path.includes("engineer/")) {
                            this.$router.push({
                                name: "engineer.forms.edit",
                                params: {
                                    id: res.data.id
                                }
                            });
                        } else {
                            this.$router.push({
                                name: "office.forms.edit",
                                params: {
                                    id: res.data.id
                                }
                            });
                        }
                    },
                    err => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    }
                );
            }
        },
        deleteForm: function() {
            if (confirm("Are you sure you want to delete this form?")) {
                Service.delete(this.form).then(
                    () => {
                        this.setInfoMessage("Form successfully deleted");
                        this.$router.push({ name: "office.forms.search" });
                    },
                    error => {
                        this.setErrorMessage("Could not delete this form");
                        console.error(error);
                    }
                );
            }
        },
    }
};
</script>





// WEBPACK FOOTER //
// resources/assets/js/components/forms/AldiFormComponent.vue